<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form">
				<h2 class="item" v-if="isCreatingJobView">
					Новая сервисная работа
				</h2>
				<h2 class="item" v-else>
					Карточка сервисной работы
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form">
				<el-form class="base-form" :model="ruleForm" ref="jobForm" status-icon :rules="rules">
					<el-form-item label="Компонент" label-position="top" prop="component">
						<catalog-picker-component
							ref="jobComponentPicker"
							auth
							v-model="ruleForm.component"
							placeholder="Поиск компонентов, агрегатов или узлов по названию" 
							search-url="/api/servicejobcomponents"
							:catalog-tab="tabs.jobComponents"
							@catalog-item-picked="onJobComponentPicked">
							<template #label>								
								{{ ruleForm.component?.name }}
							</template>
							<template #default="scope">
								{{ scope.name }}
							</template>
						</catalog-picker-component>
					</el-form-item>		

					<el-form-item label="Сервис-операция" label-position="top" prop="operation">
						<catalog-picker-component
							ref="jobOperationPicker"
							auth
							v-model="ruleForm.operation"
							placeholder="Поиск операций по названию" 
							search-url="/api/servicejoboperations"
							:catalog-tab="tabs.jobOperations"
							@catalog-item-picked="onJobOperationPicked">
							<template #label>
								{{ ruleForm.operation?.name }}			
							</template>
							<template #default="scope">
								{{ scope.name }}
							</template>
						</catalog-picker-component>
					</el-form-item>

					<el-form-item label="Категория" label-position="top" prop="category">
						<catalog-picker-component
							ref="jobCategoryPicker"
							auth
							v-model="ruleForm.category"
							placeholder="Поиск категории работ по названию" 
							search-url="/api/servicejobcategories"
							:catalog-tab="tabs.jobCategories"
							@catalog-item-picked="onJobCategoryPicked">
							<template #label>
								{{ `${ruleForm.category?.name} ${(ruleForm.category?.costPerHour ? `(${ruleForm.category?.costPerHour} руб)` : '(без надбавки)')}` }}
							</template>
							<template #default="scope">
								{{ `${scope.name} ${(scope.costPerHour ? `(${scope.costPerHour} руб)` : '')}` }}
							</template>
						</catalog-picker-component>
					</el-form-item>					
					
					<el-form-item label="Группа работ" label-position="top">
						<catalog-picker-component
							ref="jobGroupPicker"
							auth
							v-model="ruleForm.group"
							placeholder="Поиск группы работы по названию" 
							search-url="/api/servicejobgroups"
							:catalog-tab="tabs.jobGroups"
							@catalog-item-picked="onJobGroupPicked">
							<template #label>
								{{ ruleForm.group?.name }}
							</template>
							<template #default="scope">
								{{ `${scope.name}` }}
							</template>
						</catalog-picker-component>
					</el-form-item>	

					<el-form-item label="Трудозатраты по-умолчанию (часы)" label-position="top" prop="estimationHrs">
						<el-input-number v-model="ruleForm.estimationHrs" :min="0" controls-position="right" />
					</el-form-item>	

					<el-form-item>
						<el-row style="width: 100%;">
							<el-col :span="5">
								<el-button type="primary" :disabled="jobOperationPending" @click="onFormSubmitClick">
									{{ isCreatingJobView ? 'Создать' : 'Сохранить' }}
								</el-button>
							</el-col>
							<el-col :span="5">
								<el-button @click="onFormCancelClick">
									Отменить
								</el-button>
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingJobView" style="text-align: right;">
									<el-button type="danger" :disabled="jobOperationPending" @click="onFormDeleteClick">
										Удалить сервисную работу
									</el-button>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import TabsDictionary from '@/variables/tabs';
import ServiceJobCategoryModel from '@/models/ServiceJobCategoryModel';
import ServiceJobModel from '@/models/ServiceJobModel';
import EventHelper from '@/helpers/eventsHelper';
import ServiceJobOperationModel from "@/models/ServiceJobOperationModel";
import ServiceJobComponentModel from '@/models/ServiceJobComponentModel';
import ServiceJobGroupModel from '@/models/ServiceJobGroupModel';
import JobsApiHelper from '@/helpers/api/JobsApiHelper';

interface IServiceJobFormFields {
	id: number;
	category: ServiceJobCategoryModel | null;
	operation: ServiceJobOperationModel | null;
	component: ServiceJobComponentModel | null;
	group: ServiceJobGroupModel | null;
	estimationHrs: number | null;
}

@Options({
	components: {
		ElButton, ElTable, ElLink, CatalogPickerComponent
	},
	props: {
		params: Array
	}
})
export default class ServiceJobView extends Vue {
	/** params */
	params!: Array<any>;
	isCreatingJobView = false;

	/** constants */
	tabs = TabsDictionary;
	serviceJob: ServiceJobModel | null = null;
	
	isJobExistHttpClient = axiosHelper.createInstance(store.getters.jwt);
	
	/** view-models */
	jobOperationPending = false;
	ruleForm = reactive<IServiceJobFormFields>({
		id: 0,
		category: null,
		operation: null,
		component: null,
		group: null,
		estimationHrs: 0
	});

	rules = reactive({
		category: [
			{ required: true, validator: async (a: any, b: any, c: any) => await this.serviceCategoryValidator(a,b,c), trigger: 'change'},
		],
		operation: [
			{ required: true, validator: async (a: any, b: any, c: any) => await this.serviceOperationValidator(a,b,c), trigger: 'change'},
		],
		component: [
			{ required: true, validator: async (a: any, b: any, c: any) => await this.serviceComponentValidator(a,b,c), trigger: 'change'},
		],
		estimationHrs: [
			{ validator: async (a: any, b: number | null, c: any) => await this.estimationHrsValidator(a,b,c), trigger: 'change'},
		]
	});

	mounted() {
		this.initForm();
	}

	/**
	 * init form with user's data from incoming parameters array (params)
	 */
	initForm() {
		if (!this.params?.length ||
			this.params.length < 2 ||
			this.params[1] === true) {
			this.isCreatingJobView = true;
			return;
		}

		if (this.params[1] === false &&
			this.params[0]?.id) {
			this.serviceJob = new ServiceJobModel(this.params[0]);
			this.ruleForm.id = this.serviceJob.id;
			this.setServiceComponentPickerValue(this.serviceJob.component);
			this.setServiceOperationPickerValue(this.serviceJob.operation);
			this.setServiceCategoryPickerValue(this.serviceJob.category);
			this.setServiceGroupPickerValue(this.serviceJob.group);
			this.ruleForm.estimationHrs = this.serviceJob.estimationHrs ?? 0;
			this.isCreatingJobView = false;
		}
	}

	async onFormSubmitClick() {
		const form = this.$refs.jobForm as FormInstance;
		form.validate(async (valid) => {
			if (!valid) {
				return;
			}

			let result: boolean;
			this.jobOperationPending = true;
			if (this.isCreatingJobView) {
				result = await this.addJob();
			} else {
				result = await this.updateJob();
			}

			if (result) {
				ElNotification({
					message: `Сервисная работа успешно ${this.isCreatingJobView ? 'создана' : 'обновлена'}`,
					type: 'success',
				});
				EventHelper.dispatchEvent('jobsUpdate');
				this.closeCurrentTab();
			} else {
				ElNotification({
					message: `Ошибка ${this.isCreatingJobView ? 'создания новой' : 'обновления'} сервисной работы`,
					type: 'error',
				});
			}

			this.jobOperationPending = false;
		});
	}

	onFormCancelClick() {
		this.closeCurrentTab();
	}

	/** deletes an existing service-job */
	async onFormDeleteClick() {
		ElMessageBox.confirm(
			'Сервисная работа будет удалена', {
			confirmButtonText: 'OK',
			cancelButtonText: 'Отмена',
			type: 'error',
		})
		.then(async () => {
			try {
				this.jobOperationPending = true;
				const jwt = store.getters.jwt;
				const a = axiosHelper.createInstance(jwt);
				await a.delete(`/api/servicejobs/${this.ruleForm.id}`);
				ElNotification({
					title: 'Работа удалена',
					type: 'success',
				});
				EventHelper.dispatchEvent('jobsUpdate');
				this.closeCurrentTab();	
				this.jobOperationPending = false;
			} catch (e) {
				ElNotification({
					title: 'Ошибка, работа не была удалена',
					type: 'error',
				});
				console.error(`Error deleting job #${this.ruleForm.id}`, e)
			} finally {
				this.jobOperationPending = false;
			}
		});
	}

	/** fires when user picks operation from job-operations catalog */
	onJobOperationPicked(jobOperation: ServiceJobOperationModel) {		
		this.setServiceOperationPickerValue(jobOperation);
	}		

	onJobComponentPicked(jobComponent: ServiceJobComponentModel) {
		this.setServiceComponentPickerValue(jobComponent);
	}

	onJobCategoryPicked(jobCategory: ServiceJobCategoryModel) {
		this.setServiceCategoryPickerValue(jobCategory);
	}

	onJobGroupPicked(jobGroup: ServiceJobGroupModel) {
		this.setServiceGroupPickerValue(jobGroup);
	}

	closeCurrentTab() {
		store.commit('removeTab', store.getters.tab);
	}

	/** post request for create a new job */
	async addJob() {
		const apiHelper = new JobsApiHelper();
		const isJobCreated = await apiHelper.createJob(
			store.getters.jwt,
			this.ruleForm.category!.id,
			this.ruleForm.component!.id,
			this.ruleForm.operation!.id,
			this.ruleForm.group?.id,
			this.ruleForm.estimationHrs ?? undefined
		);
		return isJobCreated ?? false;
	}

	/** put request for update an existing job */
	async updateJob() {
		const apiHelper = new JobsApiHelper();
		const isJobUpdated = await apiHelper.updateJob(
			store.getters.jwt,
			this.ruleForm.id,
			this.ruleForm.category!.id,
			this.ruleForm.component!.id,
			this.ruleForm.operation!.id,
			this.ruleForm.group?.id,
			this.ruleForm.estimationHrs ?? undefined
		);
		return isJobUpdated ?? false;
	}

	async serviceCategoryValidator(rule: any, value: string, callback: any) {
		const jobId = !this.ruleForm.id ? undefined : this.ruleForm.id;
		const catId = this.ruleForm.category?.id;

		if (!catId) {
			callback(new Error('Категория сервисной работы не может быть пустой'));
			return;
		}
	
		const compId = this.ruleForm.component?.id;
		const operationId = this.ruleForm.operation?.id;

		if (!operationId || !compId) {
			return;
		}

		if (await this.isJobExist(compId, catId, operationId, jobId)) {
			callback(new Error('Такая сервисная работа уже существует'));
		}
	}

	async serviceOperationValidator(rule: any, value: string, callback: any) {
		const jobId = !this.ruleForm.id ? undefined : this.ruleForm.id;

		if (!this.ruleForm.operation?.id) {
			callback(new Error('Cервисная операция не может быть пустой'));
			return;
		}
		
		const catId = this.ruleForm.category?.id;
		const compId = this.ruleForm.component?.id;

		if (!catId || 
			!compId) {
			return;
		}

		if (await this.isJobExist(compId, catId, this.ruleForm.operation.id, jobId)) {
			callback(new Error('Такая сервисная работа уже существует'));
		}
	}

	async serviceComponentValidator(rule: any, value: string, callback: any) {
		const jobId = !this.ruleForm.id ? undefined : this.ruleForm.id;
		const compId = this.ruleForm.component?.id;

		if (!compId) {
			callback(new Error('Сервисный компонент не может быть пуст'));
			return;
		}

		const catId = this.ruleForm.category?.id;
		const operationId = this.ruleForm.operation?.id;
		
		if (!catId || !operationId) {
			return;
		}

		if (await this.isJobExist(compId, catId, operationId, jobId)) {
			callback(new Error('Такая сервисная работа уже существует'));
		}
	}

	async estimationHrsValidator(rule: any, value: number | null, callback: any) {
		if (value && value < 0) {
			callback(new Error('Трудозатраты не могут быть меньше ноля'));
			return;
		}
	}

	setServiceComponentPickerValue(value: ServiceJobComponentModel) {
		this.ruleForm.component = value;		
		const jobComponentPickerComponent = this.$refs.jobComponentPicker as CatalogPickerComponent;
		jobComponentPickerComponent.set(value);
	}

	setServiceOperationPickerValue(value: ServiceJobOperationModel) {
		this.ruleForm.operation = value;		
		const jobOperationPickerComponent = this.$refs.jobOperationPicker as CatalogPickerComponent;
		jobOperationPickerComponent.set(value);
	}

	setServiceCategoryPickerValue(value: ServiceJobCategoryModel) {
		this.ruleForm.category = value;		
		const jobCategoryPickerComponent = this.$refs.jobCategoryPicker as CatalogPickerComponent;
		jobCategoryPickerComponent.set(value);
	}

	setServiceGroupPickerValue(value: ServiceJobGroupModel | null) {
		if (!value) {
			return;
		}

		this.ruleForm.group = value!;		
		const jobGroupPickerComponent = this.$refs.jobGroupPicker as CatalogPickerComponent;
		jobGroupPickerComponent.set(value);
	}

	async isJobExist(componentId: number, categoryId: number, operationId: number, exceptJobId?: number) {
		try {
			const r = {
				categoryId: categoryId,
				operationId: operationId,
				componentId: componentId,
				exemptId: exceptJobId
			};
			await this.isJobExistHttpClient.post('/api/servicejobs/exist', r);
			return true;
		} catch (e: any) {
			if (e?.status === 409) {
				return false;
			}
			console.error(e);
			return false;
		}
	}
}
</script>